import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Users, Scale, TrendingDown, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { fmtUSD, fmtNum, truncWallet } from "@/lib/format";
import ScoreBadge from "@/components/ScoreBadge";

/**
 * Contrarian View — displays deterministic opposing-side statistics + a
 * Claude-generated narrative explaining why disagreement matters.
 *
 * Response shape:
 *   { stats: {...}, ai: <AI Response Contract>, evidence_ids, generated_at }
 */
const CONF_COLOR = {
  High:   "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
  Medium: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  Low:    "text-rose-300 bg-rose-500/15 border-rose-500/30",
};

export default function ContrarianView({ endpoint, testid = "contrarian-view" }) {
  const [requested, setRequested] = React.useState(false);
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [endpoint],
    queryFn: async () => (await api.get(endpoint)).data,
    staleTime: 5 * 60_000,
    enabled: requested,
  });

  if (!requested) {
    return (
      <section className="border border-zinc-800 rounded-md p-4" data-testid={testid}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Scale size={16} className="text-rose-300" />
              <h3 className="font-heading font-semibold text-zinc-100 text-lg">Contrarian View</h3>
            </div>
            <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
              Generate the case against consensus when you need it. This uses an AI quota credit, so it no longer runs automatically on page load.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setRequested(true)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-100 text-zinc-950 text-xs font-heading hover:bg-white transition-colors"
            data-testid={`${testid}-load`}
          >
            <Sparkles size={13} /> Generate
          </button>
        </div>
      </section>
    );
  }

  if (isLoading || isFetching) {
    return (
      <section className="border border-zinc-800 rounded-md p-4 flex items-center gap-2 text-sm text-zinc-400"
               data-testid={testid}>
        <Loader2 size={14} className="animate-spin" />
        Loading contrarian view…
      </section>
    );
  }
  if (isError || !data) {
    return (
      <section className="border border-zinc-800 rounded-md p-4 text-sm text-rose-400"
               data-testid={testid}>
        Contrarian view unavailable.
      </section>
    );
  }

  const s = data.stats || {};
  const ai = data.ai || {};
  const noOpposition = s.opposing_trader_count === 0;

  return (
    <section className="border border-zinc-800 rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <Scale size={16} className="text-rose-300" />
        <h3 className="font-heading font-semibold text-zinc-100 text-lg">
          Contrarian View
        </h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">
          the case against consensus
        </span>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-b border-zinc-800/70"
           data-testid={`${testid}-stats`}>
        <Stat label="Consensus" value={s.dominant_direction || "—"} />
        <Stat label="Opposing side" value={s.opposing_direction || "—"} accent="rose" />
        <Stat label="Opposing traders" value={fmtNum(s.opposing_trader_count)} />
        <Stat label="Opposing exposure" value={fmtUSD(s.opposing_exposure_usd)} />
      </div>

      {/* Top opposing traders */}
      {s.top_opposing_traders?.length > 0 && (
        <div className="px-4 py-3 border-b border-zinc-800/70">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            <Users size={11} /> Top dissenters
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {s.top_opposing_traders.map((t) => (
              <Link
                key={t.trader_id}
                to={`/app/traders/${t.trader_id}`}
                className="flex items-center justify-between border border-zinc-800 rounded-sm px-2 py-1.5 hover:bg-zinc-900/60"
                data-testid={`${testid}-dissenter-${t.trader_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 truncate">
                    {t.public_username || truncWallet(t.wallet_address)}
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono-num">
                    {truncWallet(t.wallet_address)} · size {fmtNum(t.size, 2)}
                  </div>
                </div>
                <ScoreBadge score={t.trader_score} />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* AI narrative */}
      <div className="p-4">
        {noOpposition && (
          <div className="mb-3 flex items-start gap-2 text-xs text-amber-300">
            <TrendingDown size={12} className="mt-0.5" />
            <span>No tracked traders positioned against consensus.</span>
          </div>
        )}
        {ai.executive && (
          <p className="text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap"
             data-testid={`${testid}-executive`}>
            {ai.executive}
          </p>
        )}

        {(ai.evidence?.length > 0 || ai.counterpoints?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {ai.evidence?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/70 mb-2">
                  Evidence
                </div>
                <ul className="space-y-1.5" data-testid={`${testid}-evidence`}>
                  {ai.evidence.map((e, i) => (
                    <li key={i} className="flex gap-2 text-sm text-zinc-300 leading-snug">
                      <span className="text-emerald-400 mt-0.5">✓</span>
                      <span>{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ai.counterpoints?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-rose-300/70 mb-2">
                  Counterpoints
                </div>
                <ul className="space-y-1.5" data-testid={`${testid}-counterpoints`}>
                  {ai.counterpoints.map((e, i) => (
                    <li key={i} className="flex gap-2 text-sm text-zinc-300 leading-snug">
                      <span className="text-rose-400 mt-0.5">×</span>
                      <span>{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {(ai.confidence || ai.recommended_investigation?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-4 mt-4 pt-3 border-t border-zinc-800/70">
            {ai.confidence && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  Contrarian case strength
                </div>
                <span
                  data-testid={`${testid}-confidence`}
                  className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-heading ${
                    CONF_COLOR[ai.confidence] || CONF_COLOR.Medium
                  }`}
                >
                  {ai.confidence}
                </span>
                <div className="text-[10px] text-zinc-500 mt-2 leading-snug max-w-xs">
                  How strong the case against consensus is — separate from the
                  signal&apos;s own confidence rating shown at the top of this page.
                </div>
              </div>
            )}
            {ai.recommended_investigation?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-blue-300/70 mb-2">
                  Recommended Investigation
                </div>
                <ul className="space-y-1.5"
                    data-testid={`${testid}-recommended`}>
                  {ai.recommended_investigation.map((r, i) => (
                    <li key={i} className="text-sm text-zinc-300 leading-snug">
                      → {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, accent }) {
  const color =
    accent === "rose"
      ? "text-rose-300"
      : "text-zinc-100";
  return (
    <div className="px-4 py-3 border-r border-zinc-800/70 last:border-r-0">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <div className={`text-lg font-mono-num mt-1 ${color}`}>{value}</div>
    </div>
  );
}
